import { useCallback, useMemo } from 'react';
import {
  Participant,
  ParticipantField,
  ParticipantFieldCategory,
  useParticipantFieldsByCategory,
  useParticipantFieldsCategories,
  useUpdateParticipant,
} from '@app/api';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { Button, InformationBox, TitleWithIcon, useToast } from '@app/core';
import { useGetParticipantDisplayValue } from '@app/participant-fields';

interface Props {
  profile: Partial<Participant>;
  criteriaRefs: string[];
  onSubmit: () => any;
  participantId: string;
}
const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    width: '100%',
    maxWidth: theme.maxContentWidth.defaultGrid,
    margin: 'auto',

    display: 'flex',
    gap: theme.marginBase * 3,
    flexDirection: 'column',
  },
  header: {},
  title: {
    ...theme.fonts.h4,
    marginTop: 0,
    marginBottom: theme.marginBase * 2,
  },

  sectionTitle: {
    marginTop: 0,
    marginBottom: 0,
  },

  section: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,

    display: 'flex',
    gap: theme.marginBase * 2,
    flexDirection: 'column',
  },
  items: {
    display: 'flex',
    gap: theme.marginBase,
    flexDirection: 'column',
  },
  label: {
    ...theme.fonts.label,
  },

  buttonLine: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.marginBase * 3,
    gap: theme.marginBase * 3,
  },
  field: {
    ...theme.fonts.caption,
  },
  button: {
    [theme.media.mobile]: {
      width: '100%',
    },
  },
}));

export const UpdateProfile = ({
  participantId,
  profile,
  criteriaRefs,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const fields = useParticipantFieldsByCategory();
  const toast = useToast();
  const { mutateAsync: updateParticipant } =
    useUpdateParticipant(participantId);
  const getParticipantDisplayValue = useGetParticipantDisplayValue();

  const { data: categories } = useParticipantFieldsCategories();

  const displayedFieldsByCategory = useMemo(() => {
    const fieldsByCategory = {} as Record<string, ParticipantField[]>;
    for (const key in fields) {
      const cat = key as ParticipantFieldCategory;
      const toKeep = fields[cat].filter((f) => criteriaRefs.includes(f.name));
      if (toKeep.length) {
        fieldsByCategory[cat] = toKeep;
      }
    }
    return fieldsByCategory;
  }, [fields, criteriaRefs]);

  const submit = useCallback(async () => {
    try {
      const updatedData = {} as Partial<Participant>;
      for (const ref of criteriaRefs) {
        // @ts-ignore
        updatedData[ref] = profile[ref];
      }

      await updateParticipant(updatedData);
      toast.saved();
      onSubmit();
    } catch (e) {
      toast.exception(e);
    }
  }, [onSubmit, toast, updateParticipant]);

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <h1 className={classes.title}>
          {t('application.updateProfile.title')}
        </h1>
        <InformationBox
          variant="success"
          text={t('application.updateProfile.info')}
        />
      </div>

      {Object.entries(displayedFieldsByCategory).map(([category, fields]) => {
        const categoryInfo = categories?.find((c) => c.name === category);
        if (!categoryInfo) {
          return null;
        }
        return (
          <div className={classes.section}>
            <TitleWithIcon
              title={categoryInfo.title}
              icon={categoryInfo.icon}
              iconColor="blue"
              key={categoryInfo.name}
              noBorder
              className={classes.sectionTitle}
            />
            <div className={classes.items}>
              {fields.map((field) => (
                <div className={classes.field}>
                  <span className={classes.label}>{t(field.label)}: </span>
                  {getParticipantDisplayValue(profile, field)}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className={classes.buttonLine}>
        <Button
          small
          className={classes.button}
          variant="transparent"
          onClick={onSubmit}
        >
          {t('application.updateProfile.skip')}
        </Button>
        <Button small className={classes.button} withNextArrow onClick={submit}>
          {t('application.updateProfile.confirm')}
        </Button>
      </div>
    </div>
  );
};
